import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { StepDisplayState, StepOutputPayload } from "../../../api/threads/liveMessages";
import ErrorNotice from "../../../errors/ErrorNotice";
import SpinnerIcon from "../../../ui/feedback/SpinnerIcon";
import { useThreadContext } from "../page/threadPageContext";
import { EntryTools } from "./EntryFrame";
import { entriesOfBlock, type ToolStepBlock } from "./logBlocks";
import { recordedProblemError } from "./recordedProblems";
import ToolApprovalNotice from "./ToolApprovalNotice";
import { toolApprovalOf } from "./toolApproval";
import ThreadEntryView from "./ThreadEntryView";
import ToolAbout from "./ToolAbout";
import { ToolTurnOutcome, ToolValuePreview } from "./ToolTurnEntries";
import { AskAboutEntry, StepOutputBody } from "./TurnEntries";

/** A run's words while it is open, by what module C shows of it (its one turn rule, WD-4 L-2a, D2-6). */
const ACTIVITY_WORDS: Partial<Readonly<Record<StepDisplayState, string>>> = {
  approval: "threads.toolStep.awaitingApproval",
  offline: "threads.toolStep.offline",
  slot: "threads.toolStep.waitingForSlot",
  queued: "threads.toolStep.queued",
  running: "threads.toolStep.running",
};

/**
 * One tool step as one block (design review item 3): the step's name, what came out of it,
 * and its outcome when it isn't the expected one. Its request, outputs and result are under
 * Details as they are recorded, each with its own number, reference and fork, so nothing
 * is hidden more than one click away. A cited entry of the step opens Details.
 */
export default function ToolStepBlockView({ block }: { block: ToolStepBlock }) {
  const { t, i18n } = useTranslation();
  const { highlightedEntry, stepName, viewerIsHost, overview, openRuns, turnProgress } = useThreadContext();
  const entries = entriesOfBlock(block);
  const cited = highlightedEntry !== null && entries.some((entry) => entry.number === highlightedEntry);
  const [detailsOpen, setDetailsOpen] = useState(cited);
  // Each citation of one of the step's entries (a `#45` link) opens Details, so it can be scrolled to.
  const [citedBefore, setCitedBefore] = useState(highlightedEntry);
  if (highlightedEntry !== citedBefore) {
    setCitedBefore(highlightedEntry);
    if (cited) {
      setDetailsOpen(true);
    }
  }
  const latest = entries.at(-1) ?? block.request;
  const finished = block.finished?.payload ?? null;
  const turnStep = { stepKey: block.request.payload.step_key, step: block.request.payload.step };
  const { run } = block.request.payload;
  // Module C's state of the run (D2-6): an approval (TA-6), the host offline, a free slot on the host's PC
  // (X-2), queued or running, the agent's tool runs included. A run that is neither finished here nor
  // open finished elsewhere, as a branch's copy of its parent's run does.
  const turnId = block.request.payload.turn_id;
  const shown = finished === null ? openRuns.get(turnId) : undefined;
  const activity = shown === undefined ? undefined : ACTIVITY_WORDS[shown];
  // Its own live line while it works: the model it fetches first (V-4), then its count of its work (F67), as its run reports
  // them under its own turn, an agent's `run_tool` call too (R3-2).
  const live = shown === "running" ? turnProgress.get(turnId) : undefined;
  const liveLine =
    live?.modelBytes != null
      ? `${t("threads.turnActivity.fetchingModel")} · ${t("threads.turnActivity.modelReceived", { megabytes: Math.round(live.modelBytes / 1_000_000) })}`
      : live?.work != null
        ? t("threads.turnActivity.workDone", { done: live.work.done, total: live.work.total })
        : null;
  // Live approvals appear once, beside the composer's activity indicator (RL-1, F104).
  const approval = finished?.problem == null ? null : toolApprovalOf(finished.problem.code, { reference: run.tool, name: run.tool_name }, window.location.origin, turnStep.stepKey ?? null);

  return (
    <li className="group app-card space-y-2 border-l-4 border-l-[var(--app-accent)] p-3">
      <div className="flex items-center gap-2">
        <span className="app-title flex min-w-0 flex-1 items-center gap-2 text-sm font-semibold">
          {shown === "running" ? <SpinnerIcon className="h-4 w-4 shrink-0" /> : null}
          {/* A run without a step (an agent's `run_tool` call, a person's run) is named by its tool. */}
          <span className="truncate" title={turnStep.stepKey ?? run.tool}>{turnStep.stepKey === null ? run.tool_name : stepName(turnStep.stepKey, turnStep.step)}</span>
          {activity === undefined ? null : <span className="app-text-muted text-xs font-normal">{t(activity)}</span>}
          {liveLine === null ? null : <span className="app-text-muted text-xs font-normal tabular-nums">{liveLine}</span>}
        </span>
        <EntryTools entry={latest} />
      </div>
      <ToolAbout tool={run.tool} />
      {finished?.summary == null ? null : (
        <p className="text-sm">{i18n.resolvedLanguage?.startsWith("de") === true ? finished.summary.de : finished.summary.en}</p>
      )}
      {block.outputs.map((output) => (
        <OutputSummary key={output.number} payload={output.payload} entry={output.number} />
      ))}
      {approval !== null ? (
        <ToolApprovalNotice
          approval={approval}
          waiting={finished === null}
          hostConnectorId={viewerIsHost ? overview.attachedConnector : null}
        />
      ) : finished?.problem == null ? null : (
        <ErrorNotice error={recordedProblemError(finished.problem)} />
      )}
      {finished === null ? null : <ToolTurnOutcome payload={finished} />}
      <details open={detailsOpen} onToggle={(event) => { setDetailsOpen(event.currentTarget.open); }}>
        <summary className="app-link cursor-pointer text-[11px]">{t("threads.turn.details")}</summary>
        {detailsOpen ? (
          <ol className="mt-2 space-y-2">
            {entries.map((entry) => (
              <ThreadEntryView key={entry.number} entry={entry} turnStep={turnStep} />
            ))}
          </ol>
        ) : null}
      </details>
    </li>
  );
}

/** What one output says at a glance: a value's short view; a proposal or annotations as recorded. */
function OutputSummary({ payload, entry }: { payload: StepOutputPayload; entry: number }) {
  if (payload.output.output_kind === "value") {
    return (
      <AskAboutEntry entry={entry}>
        <div className="space-y-1 text-sm">
          <ToolValuePreview output={payload.output} summary={true} entry={entry} />
        </div>
      </AskAboutEntry>
    );
  }
  return <StepOutputBody payload={payload} entry={entry} />;
}
